import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaArrowRight, FaBell, FaRobot, FaTimes } from 'react-icons/fa';
import { AutopilotTransientPreview as PreviewItem, useAutopilot } from './AutopilotContext';

export const AUTOPILOT_PREVIEW_DURATION_MS = 10000;

const humanize = (value: string) =>
	String(value || '')
		.toLowerCase()
		.split('_')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

const previewContent = (preview: PreviewItem) => {
	if (preview.kind === 'ACTION') {
		const { action } = preview;
		return {
			icon: <FaRobot aria-hidden="true" />,
			metadata: [action.projectName, action.bidPackageName, action.bidderName].filter(Boolean),
			status: humanize(action.status),
			summary:
				action.summary ||
				action.reason ||
				action.impact ||
				'A new Autopilot action is ready to review.',
			title: action.question || action.title || 'New Autopilot action',
		};
	}
	const { notification } = preview;
	return {
		icon: <FaBell aria-hidden="true" />,
		metadata: [notification.projectName, notification.isRevision ? 'Revision' : ''].filter(Boolean),
		status: 'Activity update',
		summary: notification.message,
		title: notification.title,
	};
};

export const useVisibleForegroundTimeout = (
	previewId: string | null,
	onTimeout: () => void,
	duration = AUTOPILOT_PREVIEW_DURATION_MS,
) => {
	const callbackRef = useRef(onTimeout);
	useEffect(() => {
		callbackRef.current = onTimeout;
	}, [onTimeout]);

	useEffect(() => {
		if (!previewId || typeof document === 'undefined') return undefined;
		let remaining = duration;
		let visibleSince = 0;
		let timer: number | undefined;
		let windowFocused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;

		const pause = () => {
			if (timer !== undefined) window.clearTimeout(timer);
			timer = undefined;
			if (visibleSince) {
				remaining = Math.max(0, remaining - (Date.now() - visibleSince));
				visibleSince = 0;
			}
		};
		const resume = () => {
			if (!windowFocused || document.visibilityState !== 'visible' || timer !== undefined) return;
			if (remaining <= 0) {
				callbackRef.current();
				return;
			}
			visibleSince = Date.now();
			timer = window.setTimeout(() => {
				timer = undefined;
				visibleSince = 0;
				remaining = 0;
				callbackRef.current();
			}, remaining);
		};
		const onVisibilityChange = () => {
			if (document.visibilityState === 'visible') resume();
			else pause();
		};
		const onWindowBlur = () => {
			windowFocused = false;
			pause();
		};
		const onWindowFocus = () => {
			windowFocused = true;
			resume();
		};

		document.addEventListener('visibilitychange', onVisibilityChange);
		window.addEventListener('blur', onWindowBlur);
		window.addEventListener('focus', onWindowFocus);
		resume();
		return () => {
			pause();
			document.removeEventListener('visibilitychange', onVisibilityChange);
			window.removeEventListener('blur', onWindowBlur);
			window.removeEventListener('focus', onWindowFocus);
		};
	}, [duration, previewId]);
};

const AutopilotTransientPreview: FC = () => {
	const {
		dismissTransientPreview,
		isOpen,
		openTransientPreview,
		railWidth,
		transientPreviews,
	} = useAutopilot();
	const preview = Array.isArray(transientPreviews) ? transientPreviews[0] || null : null;
	const [viewportWidth, setViewportWidth] = useState(() =>
		typeof window === 'undefined' ? 1440 : window.innerWidth,
	);

	useEffect(() => {
		const update = () => setViewportWidth(window.innerWidth);
		window.addEventListener('resize', update);
		return () => window.removeEventListener('resize', update);
	}, []);

	useVisibleForegroundTimeout(preview?.id || null, () => {
		if (preview) dismissTransientPreview(preview.id);
	});

	if (!preview || typeof document === 'undefined') return null;
	const content = previewContent(preview);
	const offset = isOpen && viewportWidth >= 1280 ? railWidth + 24 : 20;
	const style = { '--autopilot-preview-right': `${offset}px` } as CSSProperties;

	return createPortal(
		<section
			aria-label="Autopilot preview"
			className="autopilot-transient-preview"
			data-chat-open={isOpen ? 'true' : 'false'}
			data-preview-kind={preview.kind.toLowerCase()}
			style={style}
		>
			<span aria-atomic="true" aria-live="polite" className="sr-only" role="status">
				New {preview.kind === 'ACTION' ? 'Autopilot action' : 'activity update'}: {content.title}
			</span>
			<div className="autopilot-transient-preview__icon">{content.icon}</div>
			<div className="autopilot-transient-preview__content">
				<div className="autopilot-transient-preview__topline">
					<span>{content.status}</span>
					<button
						aria-label="Close preview"
						onClick={() => dismissTransientPreview(preview.id)}
						type="button"
					>
						<FaTimes aria-hidden="true" />
					</button>
				</div>
				<h2>{content.title}</h2>
				{content.metadata.length > 0 && (
					<p className="autopilot-transient-preview__metadata">{content.metadata.join(' · ')}</p>
				)}
				<p className="autopilot-transient-preview__summary">{content.summary}</p>
				<button
					className="autopilot-transient-preview__open"
					onClick={() => openTransientPreview(preview)}
					type="button"
				>
					Open in Autopilot Chat <FaArrowRight aria-hidden="true" />
				</button>
			</div>
		</section>,
		document.body,
	);
};

export default AutopilotTransientPreview;
